import { Ban, CheckCircle2, RotateCcw, Tags } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { InventoryCategoryModal } from '../components/InventoryCategoryModal';
import { inventoryCategoriesApi } from '../inventoryApi';
import type { InventoryCategoryListItem } from '../inventoryTypes';
import { ITEM_KIND_LABEL } from '../inventoryTypes';

type TabKey = 'info';

/**
 * One inventory category.
 *
 * A thin profile, and that is the point: "there is nothing behind this row but its own two
 * fields" was never an exemption from having one. The count of items filed here, what the item
 * form does with the usual-kind suggestion, and who last changed the word are all things a
 * reader wants before renaming or retiring it — and none of them belong in a form.
 */
export function InventoryCategoryProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [category, setCategory] = useState<InventoryCategoryListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      inventoryCategoriesApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setCategory(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError ? caught : new ApiError('Could not load this category.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  async function restore() {
    if (!id) return;

    setRestoring(true);
    setError(null);

    try {
      await inventoryCategoriesApi.activate(id);
      load();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not restore this category.', 0),
      );
    } finally {
      setRestoring(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Logistics',
    'Inventory',
    { label: 'Settings', to: '/inventory/settings' },
    { label: 'Categories', to: '/inventory/settings/categories' },
    category?.name ?? 'Category',
  ];

  if (error && !category) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (loading || !category) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const { itemCount } = category;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="inventory-category-profile"
        header={
          <ProfileHeader
            id="inventory-category-profile-header"
            icon={<Tags className="h-6 w-6" aria-hidden="true" />}
            title={category.name}
            meta={[
              ...(category.code ? [category.code] : []),
              `${itemCount} ${itemCount === 1 ? 'item is' : 'items are'} filed under it`,
            ]}
            status={
              <StatusText tone={category.isActive ? 'active' : 'inactive'}>
                {category.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={category.createdByName}
            createdAt={category.createdAt}
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="inventory-category-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={error.message}
            />
          ) : category.isActive ? (
            <ProfileBanner
              id="inventory-category-profile-banner"
              tone="slate"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This category is offered: it appears in the Category picker on the item form, and it scopes which item types that form then offers."
            />
          ) : (
            <ProfileBanner
              id="inventory-category-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This category is deleted. It is no longer offered, and the items already filed under it keep resolving its name."
              action={
                <PermissionGate permission="inventory-categories.delete">
                  <Button
                    id="inventory-category-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void restore()}
                    disabled={restoring}
                    loading={restoring}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="inventory-category-details"
          title="Details"
          menuItems={[
            {
              key: 'edit',
              label: 'Edit',
              permission: 'inventory-categories.update',
              onClick: () => setEditing(true),
            },
          ]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Name', value: category.name },
              { label: 'Code', value: category.code, mono: category.code !== null },
              { label: 'Description', value: category.description },
              {
                label: 'Usual item kind',
                value:
                  category.defaultKind === null
                    ? 'Not set — the item form asks'
                    : `${ITEM_KIND_LABEL[category.defaultKind]} — pre-selected on the item form, and never a restriction`,
              },
              {
                label: 'Items filed here',
                /*
                  A zero is MUTED, like every other quantity: a category nothing is filed under
                  is the ordinary case for a new one. Muted, never an em dash — "nothing is
                  filed here" and "we do not know" are different facts.
                */
                value: (
                  <span className={`font-mono tabular-nums ${itemCount === 0 ? 'text-slate-300' : ''}`}>
                    {itemCount}
                  </span>
                ),
              },
              { label: 'Order', value: <span className="font-mono tabular-nums">{category.sortOrder}</span> },
            ]}
          />
        </ProfileCard>

        {/* Audit is never editable: created and updated are a record of what happened. */}
        <ProfileCard id="inventory-category-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: category.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(category.createdAt)} className="font-mono">
                    {formatDateTime(category.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: category.updatedByName },
              {
                label: 'Updated',
                value: category.updatedAt ? (
                  <time dateTime={toIso(category.updatedAt)} className="font-mono">
                    {formatDateTime(category.updatedAt)}
                  </time>
                ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <InventoryCategoryModal
          open
          editing={category}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
